import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

/** Templates scaffold requirement or design documents. */
export type TemplateDocType = 'requirement' | 'design';

/** Platform templates are the built-in SDLC scaffolds (read-only, copy-on-write
 *  on edit); custom templates are tenant-owned and fully editable. */
export type TemplateTier = 'platform' | 'custom';

export interface TemplateMetadata {
  id: string;
  name: string;
  description: string;
  docType: TemplateDocType;
  /** Lucide icon name (e.g. 'FileText'); the UI maps it to a component. */
  icon?: string;
  /** Colour key (e.g. 'violet') the UI maps to Tailwind classes. */
  color?: string;
  type: TemplateTier;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
}

/** A template's metadata plus its markdown body scaffold. */
export interface TemplateDetail {
  metadata: TemplateMetadata;
  content: string;
}

export interface SaveTemplateRequest {
  name: string;
  description?: string;
  docType: TemplateDocType;
  /** Markdown body; {name}/{date}/{author} are filled at document creation. */
  content: string;
  icon?: string;
  color?: string;
}

export const templatesApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  /** Platform ∪ tenant custom templates (metadata only). */
  async list(): Promise<TemplateMetadata[]> {
    const { data } = await api.get('/templates');
    return data.templates;
  },

  /** One template's metadata + markdown body. */
  async get(templateId: string): Promise<TemplateDetail> {
    const { data } = await api.get(`/templates/${templateId}`);
    return data;
  },

  async create(request: SaveTemplateRequest): Promise<TemplateMetadata> {
    const { data } = await api.post('/templates', request);
    return data.template;
  },

  /** Update a custom template; editing a platform id creates a custom copy
   *  (copy-on-write) and returns that new template. */
  async update(templateId: string, request: SaveTemplateRequest): Promise<TemplateMetadata> {
    const { data } = await api.put(`/templates/${templateId}`, request);
    return data.template;
  },

  async remove(templateId: string): Promise<void> {
    await api.delete(`/templates/${templateId}`);
  },
};
